<template>
  <div class="container">
    <div class="dashboard-status" :class="{ 'dashboard-status_offline': !socketsConnected }">
      <div class="dashboard-status__connection">
        <span class="dashboard-status__indicator"></span>
        {{ socketsConnected ? 'Соединение активно' : 'Переподключение' }}
      </div>

      <label class="dashboard-status__debug">
        <span>Debug кандидатов</span>
        <ToggleSwitch v-model="showCandidateDebug" />
      </label>
    </div>

    <section class="artefact-filter-bar">
      <label class="artefact-filter-bar__toggle">
        <ToggleSwitch v-model="onlyGoodLots" />
        <span>Ликвидность и цена ниже медианы</span>
      </label>

      <div class="artefact-filter-bar__control">
        <label>Окно продаж</label>
        <SelectButton
          v-model="liquidityWindow"
          :options="liquidityWindowOptions"
          optionLabel="label"
          optionValue="value"
          :allowEmpty="false"
        />
      </div>

      <div class="artefact-filter-bar__control">
        <label for="minLiquiditySales">Минимум продаж</label>
        <InputNumber
          v-model="minLiquiditySales"
          inputId="minLiquiditySales"
          :min="1"
          :max="200"
          showButtons
        />
      </div>

      <div class="artefact-filter-bar__control">
        <label for="minBuyoutProfit">Минимум профита</label>
        <InputNumber
          v-model="minBuyoutProfit"
          inputId="minBuyoutProfit"
          :min="0"
          :step="10000"
          showButtons
          :useGrouping="true"
        />
      </div>
    </section>

    <artefact-dashboard
      :enteredProfit="enteredProfit"
      :only-good-lots="onlyGoodLots"
      :liquidity-window="liquidityWindow"
      :min-liquidity-sales="effectiveMinLiquiditySales"
      :min-buyout-profit="effectiveMinBuyoutProfit"
    />

    <artefact-candidate-debug
      v-if="showCandidateDebug"
      :only-good-lots="onlyGoodLots"
      :liquidity-window="liquidityWindow"
      :min-liquidity-sales="effectiveMinLiquiditySales"
      :min-buyout-profit="effectiveMinBuyoutProfit"
    />

    <artefact-dashboard-bid is-bid-variant :enteredProfit="enteredProfit" />

    <div class="input-wrapper">
      <label for="desiredProfit">Желаемый профит со ставок</label>
      <InputNumber v-model="enteredProfit" inputId="desiredProfit" class="p-inputnumber" :min="0" />
    </div>

    <artefact-dashboard-strategies
      :only-good-lots="onlyGoodLots"
      :liquidity-window="liquidityWindow"
      :min-liquidity-sales="effectiveMinLiquiditySales"
      :min-buyout-profit="effectiveMinBuyoutProfit"
    />
    <track-dashboard />

    <div class="dashboard-grid" :class="{ 'dashboard-grid_enter': mergedData.length > 0 }">
      <div v-for="item in mergedData" :key="item.id" class="dashboard-item">
        <TableSolo :item="item" :enteredProfitLots="enteredProfitLots" />
      </div>
    </div>

    <Card class="input-card">
      <template #title>Настройки</template>
      <template #content>
        <div class="input-wrapper">
          <label for="enteredProfitLots">Минимальный профит лотов</label>
          <InputNumber
            v-model="enteredProfitLots"
            inputId="enteredProfitLots"
            class="p-inputnumber"
            :min="0"
          />
        </div>
      </template>
    </Card>

    <div style="margin-bottom: 10px">
      <label>
        <input type="checkbox" v-model="isShowAdminStats" />
        Показывать админскую стату
      </label>
    </div>

    <admin-activity v-if="isShowAdminStats" />
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue'
import { useWebSocketStore } from '@/store/websocket.store'
import TableSolo from '@/components/TableSolo.vue'
import InputNumber from 'primevue/inputnumber'
import Card from 'primevue/card'
import ArtefactDashboard from '@/components/ArtefactDashboard.vue'
import ArtefactDashboardBid from '@/components/ArtefactDashboardBid.vue'
import AdminActivity from '@/components/adminActivity.vue'
import ArtefactDashboardStrategies from '@/components/ArtefactDashboardStrategies.vue'
import TrackDashboard from '@/components/TrackDashboard.vue'
import ArtefactCandidateDebug from '@/components/ArtefactCandidateDebug.vue'
import ToggleSwitch from 'primevue/toggleswitch'
import SelectButton from 'primevue/selectbutton'
import { DEFAULT_ARTEFACT_FILTER_SETTINGS } from '@/common/artefact-filters'

const wsStore = useWebSocketStore()
const enteredProfit = ref(10000)
const enteredProfitLots = ref(10000)
const socketsConnected = computed(() => wsStore.isConnected && wsStore.isArtefactConnected)
const showCandidateDebug = ref(localStorage.getItem('showCandidateDebug') !== 'false')
const onlyGoodLots = ref(
  localStorage.getItem('onlyGoodLots') === null
    ? DEFAULT_ARTEFACT_FILTER_SETTINGS.onlyLiquid
    : localStorage.getItem('onlyGoodLots') !== 'false'
)
const liquidityWindow = ref(
  Number(localStorage.getItem('liquidityWindow')) === 24
    ? 24
    : DEFAULT_ARTEFACT_FILTER_SETTINGS.liquidityWindow
)
const minLiquiditySales = ref(
  readStoredNumber('minLiquiditySales', DEFAULT_ARTEFACT_FILTER_SETTINGS.minSales)
)
const minBuyoutProfit = ref(
  readStoredNumber('minBuyoutProfit', DEFAULT_ARTEFACT_FILTER_SETTINGS.minProfit)
)
const effectiveMinLiquiditySales = computed(() =>
  Math.max(1, Number(minLiquiditySales.value) || 10)
)
const effectiveMinBuyoutProfit = computed(() => Math.max(0, Number(minBuyoutProfit.value) || 0))
const liquidityWindowOptions = [
  { label: '24 ч', value: 24 },
  { label: '48 ч', value: 48 }
]

function readStoredNumber(key, fallback) {
  const storedValue = localStorage.getItem(key)
  if (storedValue === null) return fallback

  const value = Number(storedValue)
  return Number.isFinite(value) && value >= 0 ? value : fallback
}

onMounted(() => {
  const savedValue = localStorage.getItem('enteredProfit')
  if (savedValue) {
    enteredProfit.value = Number(savedValue)
  }

  const savedValue2 = localStorage.getItem('enteredProfitLots')
  if (savedValue2) {
    enteredProfitLots.value = Number(savedValue2)
  }
})

// Сохранение значения прибыли
watch(enteredProfit, newValue => {
  localStorage.setItem('enteredProfit', newValue.toString())
})

// Сохранение значения прибыли
watch(enteredProfitLots, newValue => {
  localStorage.setItem('enteredProfitLots', newValue.toString())
})

watch(showCandidateDebug, newValue => {
  localStorage.setItem('showCandidateDebug', String(newValue))
})

watch(onlyGoodLots, newValue => {
  localStorage.setItem('onlyGoodLots', String(newValue))
})

watch(liquidityWindow, newValue => {
  localStorage.setItem('liquidityWindow', String(newValue))
})

watch(minLiquiditySales, newValue => {
  localStorage.setItem(
    'minLiquiditySales',
    String(newValue ?? DEFAULT_ARTEFACT_FILTER_SETTINGS.minSales)
  )
})

watch(minBuyoutProfit, newValue => {
  localStorage.setItem(
    'minBuyoutProfit',
    String(newValue ?? DEFAULT_ARTEFACT_FILTER_SETTINGS.minProfit)
  )
})

// Доступ к данным из стора
// const auctionData = computed(() => wsStore.auctionData);
// const priceData = computed(() => wsStore.priceStats);

const statsByPeriod = computed(() => ({
  hourly: new Map(wsStore.priceStats.hourly.map(stat => [stat.lotId, stat])),
  daily: new Map(wsStore.priceStats.daily.map(stat => [stat.lotId, stat])),
  threeDays: new Map(wsStore.priceStats.threeDays.map(stat => [stat.lotId, stat])),
  weekly: new Map(wsStore.priceStats.weekly.map(stat => [stat.lotId, stat]))
}))

const mergedData = computed(() => {
  const stats = statsByPeriod.value

  return wsStore.auctionData.map(item => {
    return {
      ...item,
      stats: {
        hourly: stats.hourly.get(item.id) || {},
        daily: stats.daily.get(item.id) || {},
        threeDays: stats.threeDays.get(item.id) || {},
        weekly: stats.weekly.get(item.id) || {}
      }
    }
  })
})

const isShowAdminStats = ref(localStorage.getItem('isShowAdminStats') !== 'false')

watch(isShowAdminStats, newValue => {
  localStorage.setItem('isShowAdminStats', newValue)
})

onMounted(() => {
  wsStore.connect()
  isShowAdminStats.value = localStorage.getItem('isShowAdminStats') !== 'false'
})
</script>

<style lang="scss">
body {
  background-color: #1e1e1e;
  color: #f0e6d2;
  font-family: 'Arial', sans-serif;
  margin: 0;
  padding: 0;
}

.auction-table {
  width: 80%;
  margin: 20px auto;
  background-color: #2c2c2c;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.4);
}

.container {
  margin: 20px auto;
  padding: 20px;
}

.input-card {
  margin-bottom: 20px;
}

.dashboard-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 95%;
  margin: 0 auto 12px;
  padding: 10px 14px;
  background: #202a23;
  border: 1px solid #356243;
  border-radius: 6px;
}

.dashboard-status_offline {
  background: #302323;
  border-color: #754444;
}

.dashboard-status__connection,
.dashboard-status__debug {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 13px;
}

.dashboard-status__indicator {
  width: 8px;
  height: 8px;
  flex: 0 0 8px;
  background: #42c976;
  border-radius: 50%;
}

.dashboard-status_offline .dashboard-status__indicator {
  background: #e05d5d;
}

.artefact-filter-bar {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) repeat(3, minmax(150px, auto));
  align-items: end;
  gap: 12px 18px;
  width: 95%;
  margin: 0 auto 12px;
  padding: 12px 14px;
  background: #242424;
  border: 1px solid #3d3d3d;
  border-radius: 6px;
}

.artefact-filter-bar__toggle {
  display: flex;
  align-items: center;
  align-self: center;
  gap: 9px;
  font-size: 13px;
}

.artefact-filter-bar__control {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.artefact-filter-bar__control > label {
  color: #aaaaaa;
  font-size: 11px;
}

.artefact-filter-bar__control .p-inputnumber {
  width: 170px;
}

@media (max-width: 640px) {
  .container {
    padding: 10px;
  }

  .dashboard-status {
    width: auto;
    align-items: flex-start;
    flex-direction: column;
  }

  .artefact-filter-bar {
    width: auto;
    grid-template-columns: 1fr;
  }

  .artefact-filter-bar__control .p-inputnumber {
    width: 100%;
  }
}

.input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
  justify-content: center;
  opacity: 0;
  max-height: 0;
  overflow: hidden;
  transition: max-height 1.5s ease, opacity 1.5s ease;

  &_enter {
    opacity: 1;
    max-height: 3000px;
  }
}
</style>
